import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import { ASSET_TYPES, ASSET_VISIBILITIES, type AdminAssetListItem } from '@/api/types'
import {
  assetsListQuery,
  useDeleteAsset,
  worldCategoriesAdminQuery,
  type AssetFilters,
} from '@/features/platform/platform.queries'
import { AssetEditDialog } from '@/features/platform/AssetEditDialog'
import { AssetPreview } from '@/features/platform/AssetPreview'
import { AssetThumbnail } from '@/features/platform/AssetThumbnail'
import { PlatformAssetUpload } from '@/features/platform/PlatformAssetUpload'
import { formatBytes, formatDate } from '@/lib/format'
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  PageHeader,
  SearchInput,
  useToast,
  type Column,
} from '@/ui'
import { IconCloudUpload, IconDelete, IconEdit, IconSchedule } from '@/icons'

const searchSchema = z.object({
  q: z.string().default(''),
  type: z.string().optional(),
  visibility: z.string().optional(),
  ownerId: z.string().optional(),
  sort: z.enum(['createdAt', 'sizeBytes', 'name']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
})

export const Route = createFileRoute('/_authed/assets/')({
  validateSearch: searchSchema,
  component: AssetsPage,
})

function AssetsPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const filters: AssetFilters = {
    q: search.q,
    type: search.type,
    visibility: search.visibility,
    ownerId: search.ownerId,
    sort: search.sort,
    order: search.order,
  }

  const query = useInfiniteQuery(assetsListQuery(filters))
  const { items, total } = flattenPages<AdminAssetListItem>(query.data)

  const categories = useQuery(worldCategoriesAdminQuery)
  const toast = useToast()
  const remove = useDeleteAsset()

  const [isUploading, setIsUploading] = useState(false)
  const [editing, setEditing] = useState<AdminAssetListItem | null>(null)
  const [previewing, setPreviewing] = useState<AdminAssetListItem | null>(null)
  const [deleting, setDeleting] = useState<AdminAssetListItem | null>(null)

  const categoryOptions = (categories.data?.items ?? []).map((category) => ({
    value: category.key,
    label: category.name,
  }))

  const columns: Column<AdminAssetListItem>[] = [
    {
      id: 'asset',
      header: t('assets.columnAsset'),
      sortKey: 'name',
      cell: (asset) => (
        <div className="flex min-w-0 items-center gap-3">
          <AssetThumbnail asset={asset} />
          <div className="min-w-0">
            <p className="truncate font-medium text-text">{asset.name}</p>
            <p className="truncate text-xs text-text-secondary">
              {t('assets.clickToPreview')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: t('assets.columnType'),
      cell: (asset) => <Badge>{t(`assets.type${asset.type}`, { defaultValue: asset.type })}</Badge>,
    },
    {
      id: 'owner',
      header: t('assets.columnOwner'),
      isSecondary: true,
      cell: (asset) =>
        asset.ownerId ? (
          <Link
            to="/users/$userId"
            params={{ userId: asset.ownerId }}
            search={{ tab: 'overview' }}
            className="truncate text-text-secondary hover:text-text hover:underline"
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            {asset.ownerDisplayName ?? asset.ownerId}
          </Link>
        ) : (
          <Badge tone="primary">{t('assets.platformOwned')}</Badge>
        ),
    },
    {
      id: 'visibility',
      header: t('assets.columnVisibility'),
      isSecondary: true,
      cell: (asset) => (
        <span className="text-text-secondary">
          {t(`assets.visibility${asset.visibility}`, { defaultValue: asset.visibility })}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('assets.columnStatus'),
      cell: (asset) =>
        asset.processingStatus === 'FAILED' ? (
          <Badge tone="danger" hasDot>
            {asset.processingStatus}
          </Badge>
        ) : asset.processingStatus === 'PENDING' || asset.processingStatus === 'UNPROCESSED' ? (
          <Badge tone="warning" hasDot>
            {asset.processingStatus}
          </Badge>
        ) : (
          <Badge tone="success" hasDot>
            {asset.processingStatus}
          </Badge>
        ),
    },
    {
      id: 'size',
      header: t('assets.columnSize'),
      sortKey: 'sizeBytes',
      align: 'end',
      cell: (asset) => (
        <span data-numeric className="text-text-secondary">
          {formatBytes(asset.byteSize, i18n.language)}
        </span>
      ),
    },
    {
      id: 'uploaded',
      header: t('assets.columnUploaded'),
      sortKey: 'createdAt',
      align: 'end',
      isSecondary: true,
      cell: (asset) => (
        <span className="text-text-secondary">{formatDate(asset.createdAt, i18n.language)}</span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      width: '3rem',
      cell: (asset) => (
        <ActionMenu
          actions={[
            {
              id: 'edit',
              label: t('common.edit'),
              icon: <IconEdit size={15} />,
              onSelect: () => {
                setEditing(asset)
              },
            },
            {
              id: 'delete',
              label: t('common.delete'),
              icon: <IconDelete size={15} />,
              isDestructive: true,
              onSelect: () => {
                setDeleting(asset)
              },
            },
          ]}
        />
      ),
    },
  ]

  const hasFilters = Boolean(search.q) || Boolean(search.type ?? search.visibility ?? search.ownerId)

  return (
    <>
      <PageHeader
        title={t('assets.title')}
        description={t('assets.subtitle')}
        actions={
          <>
            <Link
              to="/assets/jobs"
              search={{ status: undefined }}
              className="inline-flex h-9 items-center gap-2 rounded-control border border-border bg-surface-raised px-3.5 text-sm font-medium text-text transition-colors hover:bg-surface-sunken"
            >
              <IconSchedule size={15} />
              {t('assets.jobsTitle')}
            </Link>
            <Button
              variant="primary"
              icon={<IconCloudUpload size={15} />}
              onClick={() => {
                setIsUploading(true)
              }}
            >
              {t('assets.uploadTitle')}
            </Button>
          </>
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput
            value={search.q}
            onChange={(q) => {
              void navigate({ search: (prev) => ({ ...prev, q }) })
            }}
            placeholder={t('assets.searchPlaceholder')}
            className="min-w-56 flex-1"
          />

          <label className="sr-only" htmlFor="type-filter">
            {t('assets.type')}
          </label>
          <select
            id="type-filter"
            value={search.type ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({ ...prev, type: event.target.value || undefined }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          >
            <option value="">{t('common.all')}</option>
            {ASSET_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`assets.type${type}`)}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor="visibility-filter">
            {t('assets.visibility')}
          </label>
          <select
            id="visibility-filter"
            value={search.visibility ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({ ...prev, visibility: event.target.value || undefined }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          >
            <option value="">{t('common.all')}</option>
            {ASSET_VISIBILITIES.map((visibility) => (
              <option key={visibility} value={visibility}>
                {t(`assets.visibility${visibility}`)}
              </option>
            ))}
          </select>

          {total > 0 ? (
            <span data-numeric className="ml-auto text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(asset) => asset.id}
          caption={t('assets.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          sort={{ key: search.sort, order: search.order }}
          onSortChange={(next) => {
            void navigate({
              search: (prev) => ({ ...prev, sort: next.key as typeof search.sort, order: next.order }),
            })
          }}
          onRowClick={(asset) => {
            setPreviewing(asset)
          }}
          emptyTitle={hasFilters ? t('state.emptyFiltered') : t('assets.empty')}
          emptyDescription={hasFilters ? t('state.emptyFilteredHint') : undefined}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => {
            void query.fetchNextPage()
          }}
        />
      </Card>

      <PlatformAssetUpload
        isOpen={isUploading}
        onClose={() => {
          setIsUploading(false)
        }}
        categories={categoryOptions}
      />

      <AssetPreview
        asset={previewing}
        onClose={() => {
          setPreviewing(null)
        }}
      />

      <AssetEditDialog
        key={editing?.id ?? "none"}
        asset={editing}
        onClose={() => {
          setEditing(null)
        }}
        categories={categoryOptions}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null)
        }}
        onConfirm={() => {
          if (!deleting) return
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('assets.deleted'))
              setDeleting(null)
            },
            onError: (error) => {
              toast.fromError(error, t('state.errorTitle'))
            },
          })
        }}
        title={t('assets.deleteConfirm')}
        body={t('assets.deleteBody', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />
    </>
  )
}
